import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { projectApiService, Project } from '../../recruiter/services/projectApi';
import { MoreVertical, Search, Pencil, Trash2, X, Folder, Plus, ChevronDown } from 'lucide-react';

/**
 * Projects Page
 *
 * Lists all projects in a card/folder view
 * User must select a project to view jobs within it
 */
const Projects: React.FC = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [creating, setCreating] = useState(false);
    const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const projectsPerPage = 8;

    useEffect(() => {
        fetchProjects();
    }, [currentPage]);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredProjects(projects);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = projects.filter((project) =>
                project.name.toLowerCase().includes(query)
            );
            setFilteredProjects(filtered);
            setCurrentPage(1);
        }
    }, [searchQuery, projects]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setActiveMenuId(null);
        if (activeMenuId) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [activeMenuId]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await projectApiService.listProjects(currentPage, projectsPerPage);
            setProjects(response.data);
            setFilteredProjects(response.data);
            setTotalPages(response.pagination.totalPages);
        } catch (err) {
            console.error('Failed to fetch projects:', err);
            setError('Failed to load projects. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProject = async () => {
        if (!newProjectName.trim()) {
            alert('Please enter a project name');
            return;
        }

        try {
            setCreating(true);
            await projectApiService.createProject(newProjectName.trim());
            setShowCreateModal(false);
            setNewProjectName('');
            fetchProjects();
        } catch (err) {
            console.error('Failed to create project:', err);
            alert('Failed to create project. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    const handleProjectClick = (projectId: string) => {
        navigate(`/companies/projects/${projectId}`);
    };

    const handleOpenRenameModal = (project: Project) => {
        setSelectedProject(project);
        setRenameValue(project.name);
        setActiveMenuId(null);
        setShowRenameModal(true);
    };

    const handleRenameProject = async () => {
        if (!selectedProject || !renameValue.trim()) return;

        try {
            setIsRenaming(true);
            await projectApiService.updateProject(selectedProject.projectId, renameValue.trim());
            setShowRenameModal(false);
            setSelectedProject(null);
            setRenameValue('');
            fetchProjects();
        } catch (err) {
            console.error('Failed to rename project:', err);
            alert('Failed to rename folder. Please try again.');
        } finally {
            setIsRenaming(false);
        }
    };

    const handleOpenDeleteModal = (project: Project) => {
        setSelectedProject(project);
        setActiveMenuId(null);
        setShowDeleteModal(true);
    };

    const handleDeleteProject = async () => {
        if (!selectedProject) return;

        try {
            setIsDeleting(true);
            await projectApiService.deleteProject(selectedProject.projectId);
            setShowDeleteModal(false);
            setSelectedProject(null);
            fetchProjects();
        } catch (err) {
            console.error('Failed to delete project:', err);
            alert('Failed to delete folder. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages: (number | string)[] = [];
        pages.push(1);

        let startPage = Math.max(2, currentPage - 1);
        let endPage = Math.min(totalPages - 1, currentPage + 1);

        if (startPage > 2) pages.push('...');
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        if (endPage < totalPages - 1) pages.push('...');
        if (totalPages > 1) pages.push(totalPages);

        return (
            <div className="flex justify-center items-center gap-5 mt-6">
                <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ width: '32px', height: '32px', borderRadius: '8px' }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="#757575" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {pages.map((page, index) =>
                    page === '...' ? (
                        <span
                            key={`ellipsis-${index}`}
                            style={{ fontSize: '16px', lineHeight: '19px', fontWeight: 500, color: '#A5A5A5' }}
                        >
                            ...
                        </span>
                    ) : (
                        <button
                            key={page}
                            onClick={() => setCurrentPage(page as number)}
                            className="flex items-center justify-center"
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                lineHeight: '19px',
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                color: currentPage === page ? '#0B111E' : 'rgba(11, 17, 30, 0.2)',
                                border: currentPage === page ? '1px solid #015FA6' : 'none',
                                background: 'transparent'
                            }}
                        >
                            {page}
                        </button>
                    )
                )}

                <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ width: '32px', height: '32px', borderRadius: '8px' }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="#757575" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>
        );
    };

    return (
        <CompanyLayout>
            <div
                className="flex flex-col items-start bg-white rounded-2xl"
                style={{ fontFamily: 'Manrope, sans-serif', padding: '24px', gap: '16px' }}
            >
                {/* Header */}
                <div className="flex justify-between items-center w-full">
                    <h1 className="text-2xl font-bold text-slate-900">Your Projects</h1>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="bg-[#015FA6] hover:bg-[#014a85] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium text-sm"
                    >
                        <Plus size={18} />
                        New Project
                    </button>
                </div>

                {/* Search & Filter Bar */}
                <div className="flex gap-4 w-full items-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search with project title or keyword..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6] text-sm transition-all"
                        />
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-600 cursor-pointer hover:text-slate-900 text-sm font-medium">
                        <span>Active Projects</span>
                        <ChevronDown size={16} />
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="w-full text-center py-12">
                        <p className="text-gray-500">Loading projects...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="w-full text-center py-12">
                        <p className="text-red-500">{error}</p>
                        <button
                            onClick={fetchProjects}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && filteredProjects.length === 0 && (
                    <div className="w-full text-center py-12">
                        <p className="text-gray-500 mb-4">
                            {searchQuery
                                ? 'No projects found matching your search.'
                                : 'No projects yet. Create your first project to get started!'}
                        </p>
                        {!searchQuery && (
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Create Project
                            </button>
                        )}
                    </div>
                )}

                {/* Projects List */}
                {!loading && !error && filteredProjects.length > 0 && (
                    <>
                        <div className="w-full flex flex-col gap-2">
                            {filteredProjects.map((project, index) => {
                                const projectId = project.projectId || project._id || '';
                                const isMenuOpen = activeMenuId === projectId;
                                const colors = ['bg-blue-600', 'bg-emerald-500', 'bg-orange-500', 'bg-purple-500'];
                                const color = colors[index % colors.length];
                                const jobCount = project.jobIds?.length || 0;

                                return (
                                    <div
                                        key={projectId}
                                        onClick={() => handleProjectClick(projectId)}
                                        className="group relative flex items-center gap-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all duration-200 px-4 py-3.5 cursor-pointer"
                                    >
                                        {/* Color Strip */}
                                        <div className={`absolute left-0 top-3 bottom-3 w-1 ${color} rounded-r-full`} />

                                        {/* Folder Icon */}
                                        <div className="text-slate-400 group-hover:text-[#015FA6] transition-colors ml-2 flex-shrink-0">
                                            <Folder size={24} strokeWidth={1.5} />
                                        </div>

                                        {/* Project Name */}
                                        <h3 className="font-semibold text-slate-800 text-sm truncate flex-1">{project.name}</h3>

                                        {/* Job Count */}
                                        <span className="text-xs text-slate-500 flex-shrink-0 mr-2">
                                            {jobCount} {jobCount === 1 ? 'job' : 'jobs'}
                                        </span>

                                        {/* Options Menu */}
                                        <div className="relative flex-shrink-0">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveMenuId(isMenuOpen ? null : projectId);
                                                }}
                                                className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-100 transition-all duration-200"
                                            >
                                                <MoreVertical size={18} className="text-gray-500" />
                                            </button>

                                            {isMenuOpen && (
                                                <div
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute top-full right-0 mt-1 bg-white rounded-xl py-1 min-w-[160px] z-50 shadow-lg border border-slate-100"
                                                >
                                                    <button
                                                        onClick={() => handleOpenRenameModal(project)}
                                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#0B111E] hover:bg-gray-50 transition-colors"
                                                    >
                                                        <Pencil size={16} className="text-gray-500" />
                                                        <span className="text-sm">Rename</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenDeleteModal(project)}
                                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-600 hover:bg-red-50 transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                        <span className="text-sm">Delete</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {renderPagination()}
                    </>
                )}
            </div>

            {/* Create Project Modal */}
            {showCreateModal && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    onClick={() => {
                        setShowCreateModal(false);
                        setNewProjectName('');
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl p-6 w-full max-w-md"
                        style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)' }}
                    >
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-[#0B111E] mb-1">Create New Folder</h2>
                                <p className="text-gray-500 text-sm">Organize your job postings into folders</p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewProjectName('');
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-[#0B111E] mb-2">
                                Folder Name
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., Q1 Engineering Hiring"
                                value={newProjectName}
                                onChange={(e) => setNewProjectName(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleCreateProject();
                                }}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6] transition-all"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewProjectName('');
                                }}
                                className="flex-1 px-4 py-3 border border-gray-200 text-[#0B111E] rounded-xl hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateProject}
                                disabled={creating || !newProjectName.trim()}
                                className="flex-1 px-4 py-3 bg-[#015FA6] text-white rounded-xl hover:bg-[#014a82] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                                {creating ? 'Creating...' : 'Create Folder'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Modal */}
            {showRenameModal && selectedProject && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    onClick={() => {
                        setShowRenameModal(false);
                        setSelectedProject(null);
                        setRenameValue('');
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl p-6 w-full max-w-md"
                        style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)' }}
                    >
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-[#0B111E] mb-1">Rename Folder</h2>
                                <p className="text-gray-500 text-sm">Enter a new name for this folder</p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowRenameModal(false);
                                    setSelectedProject(null);
                                    setRenameValue('');
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-[#0B111E] mb-2">
                                Folder Name
                            </label>
                            <input
                                type="text"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleRenameProject();
                                }}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6] transition-all"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowRenameModal(false);
                                    setSelectedProject(null);
                                    setRenameValue('');
                                }}
                                className="flex-1 px-4 py-3 border border-gray-200 text-[#0B111E] rounded-xl hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRenameProject}
                                disabled={isRenaming || !renameValue.trim()}
                                className="flex-1 px-4 py-3 bg-[#015FA6] text-white rounded-xl hover:bg-[#014a82] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                                {isRenaming ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedProject && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    onClick={() => {
                        setShowDeleteModal(false);
                        setSelectedProject(null);
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl p-6 w-full max-w-md"
                        style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)' }}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <Trash2 size={24} className="text-red-600" />
                            </div>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedProject(null);
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <h2 className="text-xl font-semibold text-[#0B111E] mb-2">Delete Folder</h2>
                        <p className="text-gray-500 mb-6">
                            Are you sure you want to delete "<span className="font-medium text-[#0B111E]">{selectedProject.name}</span>"?
                            Jobs in this folder will be unlinked but not deleted.
                        </p>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedProject(null);
                                }}
                                className="flex-1 px-4 py-3 border border-gray-200 text-[#0B111E] rounded-xl hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteProject}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </CompanyLayout>
    );
};

export default Projects;
